import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import BrandForm from '../components/BrandForm';
import ConfirmationModal from '../components/ConfirmationModal';
import { useToast } from '../hooks/useToast';

const API_PREFIX = '/internal/filament-brands';

export default function AdminBrands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    showInSite: 'all',
  });

  const { showToast } = useToast();

  const fetchBrands = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API_PREFIX}?limit=500`);
      setBrands(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch system brands');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const filteredBrands = (() => {
    let filtered = [...brands];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(b =>
        b.name?.toLowerCase().includes(search) ||
        b.key?.toLowerCase().includes(search) ||
        b.websiteUrl?.toLowerCase().includes(search)
      );
    }

    if (filters.showInSite !== 'all') {
      filtered = filtered.filter(b => b.showInSite === (filters.showInSite === 'true'));
    }

    return filtered;
  })();

  const handleAdd = () => {
    setEditingBrand(null);
    setShowForm(true);
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setShowForm(true);
  };

  const handleDelete = (brand) => {
    setDeleteTarget(brand);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${API_PREFIX}/${deleteTarget._id}`);
      showToast(`Brand "${deleteTarget.name}" deactivated`, 'success');
      setDeleteTarget(null);
      fetchBrands();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete brand', 'error');
    }
  };

  const handleSave = async (data) => {
    const isEditing = !!editingBrand;
    if (isEditing) {
      await api.patch(`${API_PREFIX}/${editingBrand._id}`, data);
    } else {
      await api.post(API_PREFIX, data);
    }
    setShowForm(false);
    setEditingBrand(null);
    fetchBrands();
    showToast(
      isEditing ? 'System brand updated successfully!' : 'System brand created successfully!',
      'success'
    );
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'key', label: 'Key', sortable: true },
    {
      key: 'websiteUrl',
      label: 'Website',
      render: (val) => val ? (
        <a
          href={val}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {val}
        </a>
      ) : '—'
    },
    {
      key: 'logoUrl',
      label: 'Logo',
      render: (val) => val ? (
        <img
          src={val}
          alt="Brand logo"
          className="h-8 w-auto object-contain"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : '—'
    },
    {
      key: 'showInSite',
      label: 'Show in Site',
      render: (val) => (
        <span className={`px-2 py-1 text-xs font-medium rounded ${val ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {val ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (val) => new Date(val).toLocaleDateString()
    }
  ];

  const renderFilters = () => (
    <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
      <input
        type="text"
        placeholder="Search by name, key, or website..."
        value={filters.search}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        style={{ flex: '1 1 300px' }}
      />
      <select
        value={filters.showInSite}
        onChange={(e) => setFilters({ ...filters, showInSite: e.target.value })}
        style={{ flex: '1 1 150px' }}
      >
        <option value="all">All Visibility</option>
        <option value="true">Show in Site</option>
        <option value="false">Hidden from Site</option>
      </select>
    </div>
  );

  if (loading && brands.length === 0) return <div>Loading...</div>;
  if (error && brands.length === 0) return <div className="error">{error}</div>;

  return (
    <div>
      {showForm ? (
        <BrandForm
          brand={editingBrand}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingBrand(null);
          }}
        />
      ) : (
        <DataTable
          title="System Brand Presets"
          data={filteredBrands}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={handleAdd}
          renderFilters={renderFilters}
        />
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Delete System Brand"
          message={`Are you sure you want to deactivate "${deleteTarget.name}"? Operators who already duplicated it will not be affected.`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          confirmText="Deactivate"
        />
      )}
    </div>
  );
}
